import { useState, useMemo } from "react";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import TableSearch from "./TableSearch";
import TableEntrySelector from "./TableEntrySelector";
import Pagination from "./Pagination";

const Table = ({
  columns,
  data = [],
  totalEntries,
  currentPage: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  searchQuery: controlledSearchQuery,
  onSearchChange,
  rowsPerPageOptions = [10, 20, 30],
  headerClassName = "bg-[#e5f5f4]",
  headerTextClassName = "text-gray-800",
  rowClassName = "odd:bg-white even:bg-gray-100",
  language = false,
}) => {
  // Internal state for client-side mode if parent doesn't control props
  const isControlled =
    controlledPage !== undefined &&
    controlledRowsPerPage !== undefined &&
    onPageChange &&
    onRowsPerPageChange;

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchQuery, setSearchQuery] = useState("");

  // Determine active values
  const activePage = isControlled ? controlledPage : page;
  const activeRowsPerPage = isControlled ? controlledRowsPerPage : rowsPerPage;
  const activeSearchQuery = isControlled ? controlledSearchQuery : searchQuery;

  // ✅ Search filtering (client-side)
  const filteredData = useMemo(() => {
    if (isControlled) return data;
    return data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(activeSearchQuery.toLowerCase()),
      ),
    );
  }, [data, activeSearchQuery, isControlled]);

  const total = isControlled ? totalEntries : filteredData.length;
  const totalPages = Math.ceil(total / activeRowsPerPage);

  const startIndex = (activePage - 1) * activeRowsPerPage;
  const endIndex = Math.min(startIndex + activeRowsPerPage, total);
  const paginatedData = isControlled
    ? data
    : filteredData.slice(startIndex, endIndex);

  // ✅ Handlers
  const handlePageChange = (p) => {
    if (isControlled) onPageChange(p);
    else setPage(p);
  };

  const handleRowsPerPageChange = (r) => {
    if (isControlled) {
      onRowsPerPageChange(r);
      if (activePage !== 1) {
        onPageChange(1);
      }
    } else {
      setRowsPerPage(r);
      setPage(1);
      onPageChange(1);
    }
  };

  const handleSearchChange = (q) => {
    if (isControlled) {
      onSearchChange(q.target.value);
      if (activePage !== 1) {
        onPageChange(1);
      }
    } else {
      setSearchQuery(q.target.value);
      setPage(1);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow-md mx-auto">
      {/* Top Controls */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
        <TableEntrySelector
          options={rowsPerPageOptions}
          value={activeRowsPerPage}
          onChange={handleRowsPerPageChange}
          language={language}
        />
        <TableSearch
          value={activeSearchQuery}
          onChange={handleSearchChange}
          language={language}
        />
      </div>

      <div className="text-sm text-gray-600 mt-2 mb-6">
        {total > 0 ? (
          <>
            {language ? "प्रदर्शित" : "Showing"}{" "}
            <span className="font-semibold">{startIndex + 1}</span>{" "}
            {language ? "से" : "to"}{" "}
            <span className="font-semibold">{endIndex}</span>{" "}
            {language ? "कुल" : "of"}{" "}
            <span className="font-semibold">{total}</span>{" "}
            {language ? "परिणाम" : "results"}
          </>
        ) : language ? (
          "कोई परिणाम नहीं मिला"
        ) : (
          "No results found"
        )}
      </div>

      {/* Table */}
      <div className="overflow-auto mb-4">
        <table className="border border-gray-400 divide-y divide-gray-200 border border-gray-300 w-full">
          <TableHeader
            columns={columns}
            headerClassName={headerClassName}
            headerTextClassName={headerTextClassName}
            language={language}
          />
          <TableBody
            columns={columns}
            data={paginatedData}
            rowClassName={rowClassName}
          />
        </table>
      </div>

      {/* Pagination + Info */}
      <div className="flex justify-between items-center flex-wrap gap-2 p-4">
        <div className="text-sm text-gray-600 mt-2">
          {total > 0 ? (
            <>
              {language ? "प्रदर्शित" : "Showing"}{" "}
              <span className="font-semibold">{startIndex + 1}</span>{" "}
              {language ? "से" : "to"}{" "}
              <span className="font-semibold">{endIndex}</span>{" "}
              {language ? "कुल" : "of"}{" "}
              <span className="font-semibold">{total}</span>{" "}
              {language ? "परिणाम" : "results"}
            </>
          ) : language ? (
            "कोई परिणाम नहीं मिला"
          ) : (
            "No results found"
          )}
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={activePage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            language={language}
          />
        )}
      </div>
    </div>
  );
};

export default Table;
